import {css, StyleDeclaration, StyleDeclarationValue, StyleSheet} from 'aphrodite';
import React from 'react';

import {BulkAlignmentCreationKind} from 'constants/AlignmentConstants';
import BioentityChip from 'ui-library/chips/BioentityChip';
import Chip from 'ui-library/chips/Chip';
import {SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import {SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {
  AlignmentInput,
  AlignmentSetup,
  BioentityInput,
  ConsensusSetup,
  getIconForInput,
  InputType,
  TemplateSetup,
} from './createSequenceAlignmentsModalReducer';

const BASIC_CHIP: StyleDeclaration = {
  display: 'inline-block',
  maxWidth: '25ch',
  whiteSpace: 'nowrap',
  margin: '4px',
};

export function getInputItemKey(item: AlignmentInput): string {
  switch (item.type) {
    case InputType.TEXT:
    case InputType.BIOENTITY: {
      return item.id;
    }
    case InputType.FILE: {
      return item.sequenceDictURL;
    }
    default: {
      return '';
    }
  }
}

export interface DisabledOptionProps {
  disabled?: boolean;
  disabledText?: string;
}

// aphrodite doesn't export this for some reason
type CSSInputTypes = StyleDeclarationValue | false | null | void;

/**
 * Type narrower for AlignmentSetup that checks for TemplateSetups.
 * @param setup AlignmentSetup to type narrow to TemplateSetup.
 */
// eslint-disable-next-line benchlint/no-in-operator
export const isTemplateSetup = (setup: AlignmentSetup): setup is TemplateSetup => 'template' in setup;

/**
 * Type narrower for AlignmentInput that checks for BioentityInputs.
 * @param input AlignmentInput to type narrow to BioentityInput.
 */
export const isBioentityInput = (input: AlignmentInput): input is BioentityInput =>
  input.type === InputType.BIOENTITY;

export function getInputChip<T extends SmartChipType>(
  item: AlignmentInput,
  chipProps: Pick<SharedSmartChipProps<T>, 'size' | 'onRemove'>,
  additionalStyles: CSSInputTypes
): JSX.Element {
  const sharedProps = {
    className: css(styles.basicChip, additionalStyles),
    ...chipProps,
  };

  if (item.type === InputType.BIOENTITY) {
    return <BioentityChip {...sharedProps} id={item.id} key={getInputItemKey(item)} />;
  } else {
    return (
      <span title={item.name} key={getInputItemKey(item)}>
        <Chip {...sharedProps} text={item.name} icon={getIconForInput(item)} />
      </span>
    );
  }
}

export function inputToOptionData(
  input: AlignmentInput,
  getDisabledProps: (input: AlignmentInput) => {disabled?: boolean; disabledText?: string}
): OptionData {
  return {
    name: input.name,
    value: input,
    icon: getIconForInput(input),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root'},
    ...getDisabledProps(input),
  };
}

/**
 * Shortcut for picking the right set of alignment setups from configuration based on the selected alignment kind.
 * @param alignmentKind Currently selected alignment kind.
 * @param pairwiseConfiguration Current pairwise configuration.
 * @param multisequenceConfiguration Current multisequence configuration
 * @param consensusConfiguration Current consensus configuration.
 */
export function getSelectedSetups(
  alignmentKind: BulkAlignmentCreationKind,
  pairwiseConfiguration: Array<TemplateSetup>,
  multisequenceConfiguration: Array<TemplateSetup>,
  consensusConfiguration: Array<ConsensusSetup>
): Array<AlignmentSetup> {
  switch (alignmentKind) {
    case BulkAlignmentCreationKind.PAIRWISE:
      return pairwiseConfiguration;
    case BulkAlignmentCreationKind.MULTISEQUENCE:
      return multisequenceConfiguration;
    case BulkAlignmentCreationKind.CONSENSUS:
      return consensusConfiguration;
    default:
      throw new Error(`Unexpected alignment kind: ${alignmentKind}`);
  }
}

/**
 * Checks if an alignment setup requires a folder.
 *
 * A folder is required if the alignment will create a new sequence, which happens when a template is
 * not already a bioentity, or during a new consensus alignment.
 * @param alignmentSetup Alignment setup.
 */
export function requiresFolderId(alignmentSetup: AlignmentSetup): boolean {
  return (
    !isTemplateSetup(alignmentSetup) ||
    (isTemplateSetup(alignmentSetup) && !isBioentityInput(alignmentSetup.template))
  );
}

/**
 * Checks if an alignment setup is valid and ready to submit.
 *
 * @param alignmentSetup: Alignment setup.
 * @param hasFolder: Whether an output folder is specified.
 * @param isRealign: Whether this setup is a realignment.
 */
export function isValidSetup(
  alignmentSetup: AlignmentSetup,
  hasFolder: boolean,
  isRealign: boolean
): boolean {
  const minItems = isTemplateSetup(alignmentSetup) ? 1 : 2;
  let nucleotideTypesValid = true;
  if (!isTemplateSetup(alignmentSetup)) {
    nucleotideTypesValid = alignmentSetup.items.every(
      (item) => item.nucleotideType === alignmentSetup.nucleotideType
    );
  }
  return (
    alignmentSetup.items.length >= minItems &&
    (isRealign || !requiresFolderId(alignmentSetup) || hasFolder) &&
    nucleotideTypesValid
  );
}

/**
 * Builds default consensus configuration given a set of inputs. Inputs should be bucketed by nucleotide
 * type, i.e. if all inputs are DNA or all inputs are RNA there should be one group by default; if there
 * is a mix of DNA and RNA inputs, there should be a DNA group and an RNA group.
 *
 * @param inputs: An array of alignment inputs
 */
export function buildDefaultConsensusConfiguration(inputs: Array<AlignmentInput>): Array<ConsensusSetup> {
  if (inputs.length === 0) {
    return [];
  }

  const nucleotideTypes = new Set(inputs.map((input) => input.nucleotideType));
  return Array.from(nucleotideTypes).map((nucleotideType) => ({
    name: `Untitled ${nucleotideType} Consensus`,
    items: inputs.filter((item) => item.nucleotideType === nucleotideType),
    nucleotideType,
  }));
}

const styles = StyleSheet.create({
  basicChip: BASIC_CHIP,
});
